/** Etichette e toni per lo storico resoconti. */

import type { ReportKind } from './api';

export const KIND_LABELS: Record<ReportKind, string> = {
  analysis: 'Analisi titolo',
  briefing: 'Riepilogo mercato',
  digest: 'Digest di portafoglio',
  // Il backend emette anche `rotation` (F-075) e questo elenco non lo
  // conosceva: la riga usciva con un badge VUOTO e sotto la stringa grezza
  // `discovery_rotation`. Trovato ridisegnando proprio questa cella.
  rotation: 'Rotazione scoperta',
};

export const KIND_TONE: Record<ReportKind, 'accent' | 'neutral' | 'positive'> = {
  analysis: 'neutral',
  briefing: 'accent',
  rotation: 'neutral',
  // Distinto dagli altri due anche a colpo d'occhio: è il solo genere da cui
  // può nascere un ordine automatico.
  digest: 'positive',
};

export const STATUS_LABELS: Record<string, string> = {
  pending: 'In coda',
  running: 'In corso',
  completed: 'Completato',
  failed: 'Fallito',
  collecting: 'In raccolta',
  summarizing: 'In elaborazione',
};

export const STATUS_TONE: Record<string, 'positive' | 'negative' | 'neutral' | 'accent'> = {
  pending: 'neutral',
  running: 'accent',
  completed: 'positive',
  failed: 'negative',
  collecting: 'accent',
  summarizing: 'accent',
};

export const REPORT_TYPE_LABELS: Record<string, string> = {
  stock: 'Azione/ETF',
  crypto: 'Crypto',
  market_briefing: 'Briefing di mercato',
  portfolio_digest: 'Digest di portafoglio',
  discovery_rotation: 'Rotazione della scoperta',
};

export const TRIGGERED_BY_LABELS: Record<string, string> = {
  manual: 'Manuale',
  market_open_trigger: 'Trigger di mercato',
};

/**
 * Riga di dettaglio sotto il badge del genere, **oppure `null`**.
 *
 * Serviva a dire una cosa in piu' e per tre generi su quattro ripeteva quella
 * gia' scritta sopra: «Digest di portafoglio» sotto il badge «Digest di
 * portafoglio», «Briefing di mercato» sotto «Riepilogo mercato». Non e' un
 * giudizio estetico ma un fatto verificabile nel servizio: `reportType` e' una
 * **costante** per `briefing` (`market_briefing`), `digest` (`portfolio_digest`)
 * e `rotation` (`discovery_rotation`) — quindi non porta informazione, e'
 * derivabile dal badge. Solo per `analysis` varia davvero (`stock`/`crypto`).
 *
 * Su uno schermo stretto quella riga costava il doppio: occupava la risorsa
 * piu' scarsa della pagina per non dire niente.
 */
export function reportTypeDetail(kind: ReportKind, reportType: string): string | null {
  if (kind !== 'analysis') return null;
  return REPORT_TYPE_LABELS[reportType] ?? reportType;
}

/**
 * Data compatta per la vista a schede.
 *
 * L'anno si omette quando e' quello corrente: in un elenco ordinato dal piu'
 * recente e' l'informazione meno utile, ed e' quella che su un telefono mandava
 * la data a capo su quattro righe («26 / ago / 2026, / 11:41»), rendendo ogni
 * riga alta come una scheda. Sugli anni passati resta, perche' li' distingue.
 */
export function formatReportDate(value: string, now: Date = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
